<template>
    <div id="patient-portal-note-package-container">
        <b-row>
            <b-col>
                <MdTitle
                    v-bind:Text="
                        this.editMode
                            ? $t('PPDbSetup.FormsPackagesTab.EditPackageTitle')
                            : $t('PPDbSetup.FormsPackagesTab.AddPackageTitle')
                    "
                />
            </b-col>
        </b-row>

        <b-row>
            <b-col lg="4" md="4" sm="12">
                <MdEdit
                    v-bind:Label="$t('Shared.Description')"
                    v-bind:TypeProp="'text'"
                    v-bind:Name="'description'"
                    v-bind:MaxLengthProp="150"
                    v-model="description"
                    v-bind:Id="'input_description'"
                    v-bind:isRequired="true"
                    v-bind:isValid="isDescriptionValid"
                    v-bind:invalidMessage="$t('Msg.RequiredField')"
                    v-on:change="pendingChanges = true"
                />
            </b-col>
        </b-row>
       <MdTitle v-bind:Text="$t('PPDbSetup.FormsPackagesTab.FormsInPackage')" v-bind:isSubtitle="true" v-bind:Name="'T_TitleSearch'"/>
        <b-row>
            <b-col class="col-12 mt-2">
                <MdEditableGrid
                    :ref="'editableGrid'"
                    :columns="columns"
                    :rows="rows"
                    :showMessageIsEmpty="true"
                    :dataKey="'recID'"
                    :showAdd="true"
                    :showDelete="true"
                    :showSelectAll="true"
                    :serverSidePagination="false"
                    :appendNewRows="appendNewRows"
                    :deleteActionButton="deleteActionButton"
                    @cell-edit-complete="cellEditComplete"
                >
                </MdEditableGrid>
            </b-col>
        </b-row>

        <div class="space-actions"></div>
        <div class="page-actions">
            <MdButton
                v-bind:Label="$t('Shared.Back')"
                v-bind:Name="'btnBack'"
                v-on:click="goBack"
                v-bind:ClassProp="'mx-2 btn-login'"
            />
            <MdButton
                v-bind:Label="$t('Shared.Save')"
                v-bind:ClassProp="'primary btn-login mx-2'"
                v-on:click="saveOrEdit"
                v-bind:isReadOnly="saving"
                v-bind:Name="'btn_save_package'"
            />
        </div>
    </div>
</template>

<script>
import PatientPortalNotePackageHdrsService from '@/services/patientPortalNotePackageHdrs.service';
import ShowMessage from '@/components/messages/ShowMessage.js';
import DBFunctions from '@/common/DBFunctions.js';
import Utils from '@/common/utils';
import validationRules from '@/common/validationRules';

export default {
    name: 'PatientPortalNotePackage',
    mixins: [ShowMessage, DBFunctions],
    props: {
        editMode: Boolean
    },
    data() {
        return {
            OptionsRightMenu: {
                visible: false,
                ItemsList: [],
                btnvisible: false,
                itemdisabled: false
            },
            description: '',
            isDescriptionValid: true,
            rows: [],
            pendingChanges: false,
            saving: false,
            columns: [
                {
                    key: 'notesid',
                    label: this.$t('Shared.Description'),
                    dataType: 'lookup',
                    editable: true,
                    focusable: true,
                    validator: [validationRules.required(this.$t('Msg.RequiredField'))],
                    props: {
                        api: 'NotesTypes?ShowInPatientPortal=true',
                        searchTitle: this.$t('PPDbForm.SearchNote'),
                        fieldCode: 'notesid',
                        fieldDescription: 'description',
                        valueDescription: 'description',
                        fields: [
                            {
                                key: 'description',
                                label: this.$t('Shared.Description')
                            }
                        ]
                    }
                }
            ],
            deleteActionButton: {
                visible: true,
                handler: this.deleteNoteRows
            }
        };
    },
    methods: {
        goBack() {
            this.$router.push({
                name: 'patients-portal-setup',
                query: { tab: 'formsPackages' }
            });
        },

        validate_required() {
            this.isDescriptionValid = !Utils.isNullOrEmptyString(this.description);
            return this.isDescriptionValid;
        },

        cellEditComplete({ rowKey, columnKey, value, lookupInfo }) {
            const rowIndex = this.rows.findIndex(row => row.recID === rowKey);
            if (rowIndex !== -1) {
                this.$set(this.rows[rowIndex], columnKey, value);
                if (columnKey === 'notesid' && lookupInfo) {
                    this.$set(this.rows[rowIndex], 'description', lookupInfo.description);
                }
                this.pendingChanges = true;
            }
        },

        appendNewRows(newRows) {
            let newKey = this.rows.length > 0 ? Math.max(...this.rows.map(row => row.recID)) + 1 : 1;
            newRows.forEach(row => {
                this.rows.push({
                    recID: newKey++,
                    notesid: row.notesid,
                    description: row.description
                });
            });
            this.pendingChanges = true;
            return true;
        },

        deleteNoteRows(item) {
            // The grid calls this with a single row from the inline per-row delete icon,
            // or with the full array of selected rows from the toolbar's bulk Delete button.
            const rowsToRemove = Array.isArray(item) ? item : [item];

            this.$bvModal
                .msgBoxConfirm(this.$t('Msg.DeleteRecord'), {
                    title: this.$tc('Shared.DelRecord', rowsToRemove.length),
                    okTitle: this.$t('Shared.TitleDelete'),
                    cancelTitle: this.$t('Shared.BtnCancel'),
                    footerClass: 'p-2',
                    titleClass: 'modal-title modal-type-warning'
                })
                .then(value => {
                    if (value) {
                        const idsToRemove = rowsToRemove.map(row => row.recID);
                        this.rows = this.rows.filter(row => !idsToRemove.includes(row.recID));
                        this.pendingChanges = true;
                    }
                });
        },

        async saveOrEdit() {
            if (!this.validate_required()) {
                this.showMessage(
                    this.$tc('Msg.Error'),
                    this.$t('Msg.CheckRedLabeledFields'),
                    'errorAutoHide'
                );
                return;
            }

            this.saving = true;

            if (!(await this.$refs.editableGrid.save())) {
                this.saving = false;
                this.showMessage(
                    this.$tc('Msg.Error'),
                    this.$t('Msg.InvalidFieldValuesDetected'),
                    'errorAutoHide'
                );
                return;
            }

            const data = {
                description: this.description,
                notesIds: this.rows.map(row => row.notesid)
            };

            const request = this.editMode
                ? PatientPortalNotePackageHdrsService.update(this.$route.params.id, data)
                : PatientPortalNotePackageHdrsService.add(data);

            await request
                .then(response => {
                    if (response.status == '200') {
                        this.pendingChanges = false;

                        setTimeout(
                            () =>
                                this.$bvToast.toast(this.$t('Shared.SavedSuccessfully'), {
                                    title: this.$t('Msg.Success'),
                                    autoHideDelay: 5000,
                                    variant: 'success'
                                }),
                            50
                        );

                        this.goBack();
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        getPackage() {
            if (this.editMode) {
                PatientPortalNotePackageHdrsService.get(this.$route.params.id)
                    .then(response => {
                        if (response.status == '200' && response.data) {
                            this.description = response.data.description;
                            this.rows = (response.data.notes || []).map((note, index) => ({
                                recID: index + 1,
                                notesid: note.notesId,
                                description: note.description
                            }));
                        }
                    })
                    .catch(error => {
                        this.showResponseErrorMessage(error);
                    });
            }
        },

        hasUnsavedChanges() {
            const grid = this.$refs.editableGrid;
            return this.pendingChanges || !!(grid && grid.d_new_rows.length > 0);
        },

        beforeWindowUnload(e) {
            if (this.hasUnsavedChanges()) {
                e.preventDefault();
                e.returnValue = '';
            }
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.hasUnsavedChanges()) {
            this.$bvModal
                .msgBoxConfirm(this.$t('Msg.LeavingWithoutSaving'), {
                    title: this.$t('PPDbSetup.Title'),
                    okTitle: this.$t('Shared.BtnSave'),
                    cancelTitle: this.$t('Shared.BtnDismiss'),
                    footerClass: 'p-2',
                    titleClass: 'modal-title modal-type-warning'
                })
                .then(async value => {
                    if (!value) {
                        next();
                    } else {
                        await this.saveOrEdit();

                        if (!this.pendingChanges) {
                            next();
                        }
                    }
                });
        } else {
            next();
        }
    },
    created() {
        this.OptionsRightMenu.ItemsList = [];
        let layoutStatus = this.$store.state.layoutStatus;
        switch (layoutStatus) {
            case 'L0R1':
                this.OptionsRightMenu.favButtonAtion = false;
                this.$emit('getVisibleMenu', this.OptionsRightMenu);
                break;
            case 'L1R0':
            case 'L1R1':
                this.OptionsRightMenu.btnvisible = false;
                this.$emit('getVisibleMenu', this.OptionsRightMenu);
                break;
            default:
                this.OptionsRightMenu.favButtonAtion = false;
                this.$emit('getVisibleMenu', this.OptionsRightMenu);
        }

        window.addEventListener('beforeunload', this.beforeWindowUnload);
    },
    mounted() {
        this.getPackage();
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.beforeWindowUnload);
    }
};
</script>
